



import { FC, useState, useEffect } from 'react';
import { useSupabase, uploadImage, deleteImage } from '../../../hooks/useSupabase';

interface AboutContent {
  id: number;
  title: string;
  description: string;
  features?: string[] | null;
  image?: string;
  image_url?: string;
  order_index?: number;
  icon?: string;
}

const AboutManagement: FC = () => {
  const { supabase } = useSupabase();
  const [contents, setContents] = useState<AboutContent[]>([]);
  const [editingContent, setEditingContent] = useState<AboutContent | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newContentImage, setNewContentImage] = useState('');

  // 초기 데이터 로드
  useEffect(() => {
    loadItems();
  }, [supabase]);

  const loadItems = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('about_content')
        .select('*')
        .order('order_index');

      if (error && error.code !== 'PGRST116') {
        console.error('소개 항목 로드 실패:', error);
        await initializeDefaultItems();
        return;
      }

      if (data && data.length > 0) {
        setContents(data);
      } else {
        await initializeDefaultItems();
      }
    } catch (error) {
      console.error('소개 항목 로드 중 오류:', error);
      await initializeDefaultItems();
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultItems = async () => {
    const defaultItems = [
      {
        title: '30년 경력의 호텔 셰프',
        description: '30년 동안 요리와 함께 한 시간이 만들어낸 정성스러운 한 그릇. 동양 재료에 대한 안목과 손님들을 위한 진심으로 특별한 맛을 경험할 수 있습니다.',
        image: 'https://public.readdy.ai/ai/img_res/e930142d5deaa0f84573801efe262de6.jpg',
        order_index: 1
      },
      {
        title: '최상급 이베리코 전문점',
        description: '스페인 올리브나무 지역의 최상급 이베리코 흑돼지만을 사용합니다. 이베리코돼지한마리 메뉴로 다양한 부위의 맛있는 맛을 만나보세요.',
        image: 'https://readdy.ai/api/search-image?query=Premium%20Iberico%20black%20pork%20cuts%20displayed%20on%20wooden%20cutting%20board%2C%20high%20quality%20marbled%20meat%20with%20rich%20texture%2C%20professional%20butcher%20preparation%2C%20traditional%20Korean%20restaurant%20kitchen%20setting&width=600&height=400&seq=iberico&orientation=landscape',
        order_index: 2
      },
      {
        title: '따뜻한 가마솥밥',
        description: '주문하면 즉석에서 정성스럽게 지어드리는 작은 가마솥밥으로 식사를 완성합니다. 갓 지은 밥의 따뜻함이 특별한 식사 경험을 만들어드립니다.',
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20stone%20pot%20rice%20with%20steam%20rising%2C%20small%20black%20ceramic%20pot%20with%20perfectly%20cooked%20white%20rice%2C%20traditional%20Korean%20restaurant%20table%20setting%2C%20warm%20cozy%20atmosphere&width=600&height=400&seq=rice&orientation=landscape',
        order_index: 3
      },
      {
        title: '편안한 95석 공간',
        description: '95석의 넓은 공간으로 가족 모임과 회식에 최적화되어 있습니다. 넓은 좌석으로 혼자 식사하기에도 부담 없는 편안한 공간입니다.',
        image: 'https://readdy.ai/api/search-image?query=Spacious%20Korean%20restaurant%20dining%20room%20with%2095%20comfortable%20seats%2C%20modern%20traditional%20interior%20design%2C%20warm%20lighting%2C%20family-friendly%20atmosphere%2C%20clean%20wooden%20tables%20and%20chairs&width=600&height=400&seq=interior&orientation=landscape',
        order_index: 4
      }
    ];

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('about_content')
          .insert(defaultItems)
          .select();

        if (error) {
          console.error('기본 소개 항목 저장 실패:', error);
        } else if (data) {
          setContents(data);
        }
      } catch (error) {
        console.error('기본 소개 항목 저장 중 오류:', error);
      }
    }
  };

  const handleEdit = (item: AboutContent) => {
    setEditingContent({ ...item });
  };

  const handleDelete = async (id: number) => {
    if (!confirm('정말로 이 소개 항목을 삭제하시겠습니까?')) return;

    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('about_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setContents(contents.filter(item => item.id !== id));
      alert('소개 항목이 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('소개 항목 삭제 실패:', error);
      alert('소개 항목 삭제에 실패했습니다.');
    }
  };

  const handleImageUpload = async (file: File, isEdit: boolean = false) => {
    if (!file) return;

    // 파일 크기 체크 (5MB 제한)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    // 파일 형식 체크
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    setUploading(true);
    try {
      // 파일명을 안전한 형태로 생성 (영문, 숫자만 사용)
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `about-${timestamp}.${fileExtension}`;
      
      const imageUrl = await uploadImage(file, fileName);
      
      if (isEdit && editingContent) {
        setEditingContent({ ...editingContent, image: imageUrl });
      } else {
        setNewContentImage(imageUrl);
      }
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (imageUrl: string, isEdit: boolean = false) => {
    if (!confirm('이미지를 삭제하시겠습니까?')) return;

    try {
      // Supabase Storage에서 이미지 삭제
      if (imageUrl.includes('supabase')) {
        const path = imageUrl.split('/').pop();
        if (path) {
          await deleteImage(path);
        }
      }

      if (isEdit && editingContent) {
        setEditingContent({ ...editingContent, image: '' });
      } else {
        setNewContentImage('');
      }
    } catch (error) {
      console.error('이미지 삭제 실패:', error);
      alert('이미지 삭제에 실패했습니다.');
    }
  };

  const handleImageGenerate = async (prompt: string, isEdit: boolean = false) => {
    if (!prompt.trim()) return;

    setUploading(true);
    try {
      const imageUrl = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=800&height=600&seq=${Date.now()}&orientation=landscape`;
      
      if (isEdit && editingContent) {
        setEditingContent({ ...editingContent, image: imageUrl });
      } else {
        setNewContentImage(imageUrl);
      }
    } catch (error) {
      console.error('이미지 생성 실패:', error);
      alert('이미지 생성에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleAddContent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const title = formData.get('title') as string;
    const description = formData.get('description') as string;

    if (!title || !description) {
      alert('제목과 설명을 모두 입력해주세요.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('about_content')
        .insert([
          {
            title,
            description,
            features: [], // features 필드를 빈 배열로 설정
            image: newContentImage || null,
            order_index: contents.length + 1
          }
        ])
        .select()
        .single();

      if (error) throw error;

      setContents([...contents, data]);
      setShowAddForm(false);
      setNewContentImage('');
      
      // 폼 초기화 - 안전하게 처리
      if (form) {
        form.reset();
      }
      
      alert('새 소개 항목이 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('소개 항목 추가 실패:', error);
      alert('소개 항목 추가에 실패했습니다.');
    }
  };

  const handleUpdateContent = async (id: number, updatedContent: AboutContent) => {
    if (!supabase) return;
    
    try {
      const { error } = await supabase
        .from('about_content')
        .update({
          title: updatedContent.title,
          description: updatedContent.description,
          features: updatedContent.features || [], // features 필드를 빈 배열로 설정
          image: updatedContent.image,
          order_index: updatedContent.order_index
        })
        .eq('id', id);

      if (error) throw error;

      setContents(contents.map(content => 
        content.id === id ? { ...content, ...updatedContent } : content
      ));
      
      alert('소개 항목이 성공적으로 수정되었습니다.');
    } catch (error) {
      console.error('소개 항목 수정 실패:', error);
      alert('소개 항목 수정에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin mb-4"></i>
          <p className="text-gray-600">소개 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">소개 내용 관리</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          내용 추가
        </button>
      </div>

      <div className="space-y-4">
        {contents.map((content) => (
          <div key={content.id} className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex gap-4">
              {content.image && (
                <img
                  src={content.image}
                  alt={content.title}
                  className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">{content.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{content.description}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(content)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(content.id as number)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    삭제
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">내용 수정</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">이미지</label>
                {editingContent.image && (
                  <div className="relative">
                    <img
                      src={editingContent.image}
                      alt="미리보기"
                      className="w-full h-48 object-cover rounded-lg mb-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleImageDelete(editingContent.image as string, true)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* 파일 업로드 */}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(file, true);
                        }
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG, GIF 파일 (최대 5MB)
                    </p>
                  </div>
                  
                  <div className="text-center text-gray-400">또는</div>
                  
                  {/* AI 이미지 생성 */}
                  <div>
                    <input
                      type="text"
                      placeholder="이미지 설명을 입력하세요 (예: 한식당 요리사가 음식을 준비하는 모습)"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleImageGenerate(e.currentTarget.value, true);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      이미지 설명을 입력하고 Enter를 누르면 AI가 이미지를 생성합니다
                    </p>
                  </div>
                </div>
                
                {uploading && (
                  <div className="flex items-center justify-center py-4">
                    <i className="ri-loader-4-line text-2xl text-blue-600 animate-spin mr-2"></i>
                    <span className="text-sm text-gray-600">이미지 처리 중...</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">제목</label>
                <input
                  type="text"
                  value={editingContent.title}
                  onChange={(e) => setEditingContent({...editingContent, title: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">내용</label>
                <textarea
                  value={editingContent.description}
                  onChange={(e) => setEditingContent({...editingContent, description: e.target.value})}
                  rows={6}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={async () => {
                  if (editingContent) {
                    await handleUpdateContent(editingContent.id as number, editingContent);
                    setEditingContent(null);
                  }
                }}
                disabled={uploading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                저장
              </button>
              <button
                onClick={() => setEditingContent(null)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">새 내용 추가</h3>
            <form onSubmit={handleAddContent}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">이미지 (선택사항)</label>
                  {newContentImage && (
                    <div className="relative">
                      <img
                        src={newContentImage}
                        alt="미리보기"
                        className="w-full h-48 object-cover rounded-lg mb-2"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageDelete(newContentImage, false)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {/* 파일 업로드 */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, false);
                          }
                        }}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        disabled={uploading}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG, GIF 파일 (최대 5MB)
                      </p>
                    </div>
                    
                    <div className="text-center text-gray-400">또는</div>
                    
                    {/* AI 이미지 생성 */}
                    <div>
                      <input
                        type="text"
                        placeholder="이미지 설명을 입력하세요 (예: 한식당 요리사가 음식을 준비하는 모습)"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleImageGenerate(e.currentTarget.value, false);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        이미지 설명을 입력하고 Enter를 누르면 AI가 이미지를 생성합니다
                      </p>
                    </div>
                  </div>
                  
                  {uploading && (
                    <div className="flex items-center justify-center py-4">
                      <i className="ri-loader-4-line text-2xl text-blue-600 animate-spin mr-2"></i>
                      <span className="text-sm text-gray-600">이미지 처리 중...</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">제목</label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">내용</label>
                  <textarea
                    name="description"
                    required
                    rows={6}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  추가
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewContentImage('');
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutManagement;



